// AI Adoption Portal — light Atlassian shell that mirrors RiskPortal.
// Wraps the existing AI Adoption components (AIInventory, AIIncidents,
// AIPolicies, etc.) in a deep operational layout with a left-rail nav and
// role-aware defaults. Built alongside the existing /ai-adoption route;
// both stay live during transition so we can fall back if needed.
//
// IA mirrors the proposal: 7 nav groups, ~22 leaf views. Each view either
// renders an existing component verbatim (most common case) or a small
// portal-specific atom wired to the same backend endpoint.

import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { ai_dashboard, ai_recompute, ai_load_cost_advisor_top_spenders, ai_load_link_reviews, getUserPlanInfoService } from '../services';
import { unwrap } from '../services/HelperMethod/aigEnvelope';
import CommonButton from '../Components/CommonButton';
import CommonSidebar from '../Components/CommonSidebar';
import CommonHeader from '../Components/CommonHeader';
import ModuleIcon from '../Components/ModuleIcon';
import AddOnCard from '../Dashboard/AddOns/AddOnCard';
import { lightTheme as T } from '../Constants/Colors';
import { fontFamily, fontSize, fontWeight } from '../Constants/Fonts';
import './AIAdoptionPortal.css';
// v2 sub-pages (ModeDashboards, FrameworkWizard, EvidenceLedger, etc.)
// use the ai_v2_* classes whose styles live in aigov_v2.scss.
// Importing here keeps the v2 stylesheet in the Portal bundle
// regardless of which sub-page the customer lands on first.
import '../styles/aigov_v2.scss';

// ── Lazy-load the existing AI Governance v1 + v2 surfaces. Lazy so the
// portal shell loads fast even though it can route into 25 different views.
const AIInventory = lazy(() => import('../AIAdoption/AIInventory'));
const AIIncidents = lazy(() => import('../AIAdoption/AIIncidents'));
const AIPolicies = lazy(() => import('../AIAdoption/AIPolicies'));
const AIRiskCompliance = lazy(() => import('../AIAdoption/AIRiskCompliance'));
const AIRiskAssessment = lazy(() => import('../AIAdoption/AIRiskAssessment'));
// AICompliance was a compliance-only standalone superseded by AIRiskCompliance
// (which already shows the same compliance-scores view via its built-in toggle).
// Deleted 2026-06-15. The `compliance` nav route below points at AIRiskCompliance.
const AIAuditLog = lazy(() => import('../AIAdoption/AIAuditLog'));
const AIWhitelist = lazy(() => import('../AIAdoption/AIWhitelist'));
const AIUserUsage = lazy(() => import('../AIAdoption/AIUserUsage'));
const AIIntegrations = lazy(() => import('../AIAdoption/AIIntegrations'));
const AIDlpIntegrations = lazy(() => import('../AIAdoption/AIDlpIntegrations'));
const AIIdentityLinkReviews = lazy(() => import('../AIAdoption/AIIdentityLinkReviews'));
const AICostAdvisorConfig = lazy(() => import('../AIAdoption/AICostAdvisorConfig'));
const AIAgentInstallation = lazy(() => import('../AIAdoption/AIAgentInstallation'));
const AIVendorCoverage = lazy(() => import('../AIAdoption/AIVendorCoverage'));
const AIPolicyGenerator = lazy(() => import('../AIAdoption/AIPolicyGenerator'));
const AIExtensionAdmin = lazy(() => import('../AIAdoption/AIExtensionAdmin'));
const AISignatures = lazy(() => import('../AIAdoption/AISignatures'));
const AIRedTeam = lazy(() => import('../AIAdoption/AIRedTeam'));
const AIDsar = lazy(() => import('../AIAdoption/AIDsar'));
const AIBias = lazy(() => import('../AIAdoption/AIBias'));
const AIGroupEnforcement = lazy(() => import('../AIAdoption/AIGroupEnforcement'));
const AIEndpointEnforcement = lazy(() => import('../AIAdoption/AIEndpointEnforcement'));
const AIIdPIntegrations = lazy(() => import('../AIAdoption/AIIdPIntegrations'));
const AILLMGateway = lazy(() => import('../AIAdoption/AILLMGateway'));
const AIAgentTrace = lazy(() => import('../AIAdoption/AIAgentTrace'));
const AIBudget = lazy(() => import('../AIAdoption/AIBudget'));
const AICostAdvisor = lazy(() => import('../AIAdoption/AICostAdvisor'));
const AICoveragePosture = lazy(() => import('../AIAdoption/AICoveragePosture'));

// Editable framework catalog (DB-driven). Single editor handles browse,
// clone, and edit — replaces the now-removed read-only FrameworkCatalog.
const FrameworkEditor = lazy(() => import('./FrameworkEditor'));
// Raw events explorer + create-rule-from-event modal.
const RawEventsView = lazy(() => import('./RawEventsView'));
const SeverityTuningView = lazy(() => import('./SeverityTuningView'));
const SIEMForwardersView = lazy(() => import('./SIEMForwardersView'));

// v2 surfaces — each is its own nav item.
const FrameworkWizard = lazy(() => import('../AIAdoption/v2/FrameworkWizard'));
const ModeDashboards = lazy(() => import('../AIAdoption/v2/ModeDashboards'));
const EvidenceLedger = lazy(() => import('../AIAdoption/v2/EvidenceLedger'));
const AuditorTokens = lazy(() => import('../AIAdoption/v2/AuditorTokens'));
// Response Eval / Sentiment / Drift were three top-level nav items each;
// consolidated 2026-06-15 into RuntimeObservability which renders all
// three under one nav slot with sub-tabs. The underlying components are
// still lazy-loaded individually inside RuntimeObservability.
const RuntimeObservability = lazy(() => import('./RuntimeObservability'));

// ── Nav definition. id is the routing key; component is the React element
// to render. badge is an optional dynamic counter (computed from dashboard
// data — open violations, pending review, etc.).
// Nav structure follows industry-standard AI Governance taxonomy:
//   - Discover     = Shadow AI Discovery (find what's running)
//   - Protect      = AI Runtime Protection (Monitor / Detect / Prevent)
//   - FinOps       = AI Spend Management (the emerging discipline tracked
//                    by the FinOps Foundation's AI working group)
//   - Compliance   = Risk + framework attestation (ISO 42001, NIST AI RMF,
//                    EU AI Act, OWASP LLM Top 10)
//   - Administer   = Deployment + integrations + audit trail
// Maps to Gartner's "AI TRiSM" framework + the FinOps Foundation's
// LLM cost-management discipline.
const NAV_GROUPS = [
    {
        label: 'Overview',
        items: [
            { id: 'home', title: 'Executive Dashboard', subtitle: 'Where AI is being adopted, how it is governed, and what to enable next' },
            { id: 'coverage', title: 'Adoption Posture', subtitle: 'Every detection layer, every uncovered person, every documented blind spot in your AI rollout' },
        ],
    },
    {
        label: 'Discover',
        items: [
            { id: 'inventory', title: 'AI Inventory', subtitle: 'Every discovered AI asset across browser, endpoint, gateway, and vendor pulls' },
            { id: 'signatures', title: 'Detection Signatures', subtitle: 'AI-tool fingerprint catalogue — system defaults + your own' },
        ],
    },
    {
        label: 'Protect',
        items: [
            { id: 'policies', title: 'Active Policies', subtitle: 'Rule library — Policies / Modes / Violations sub-tabs' },
            { id: 'group_enforcement', title: 'Group Enforcement', subtitle: 'Per-(policy, group) mode bindings — admin matrix' },
            { id: 'endpoint_enforcement', title: 'Endpoint Enforcement', subtitle: 'Per-(policy, AI endpoint) mode bindings — strictest wins' },
            { id: 'llm_gateway', title: 'LLM Gateway', subtitle: 'Identity-bound API keys + policy enforcement on every call — direct API, RPA, CI, SDK' },
            { id: 'whitelist', title: 'Allowlist', subtitle: 'Sanctioned AI tools — admin-curated allow/block list' },
            { id: 'incidents', title: 'Policy Violations', subtitle: 'Events that broke a named policy' },
        ],
    },
    {
        label: 'FinOps',
        items: [
            { id: 'budget', title: 'AI Budget', subtitle: 'Org → team → person spend caps with monthly / quarterly / annual periods. 429 cap_exceeded when reached.' },
            { id: 'cost_advisor', title: 'Cost Advisor', subtitle: 'AWS Cost Explorer for LLMs — right-sizing, caching, waste reduction with projected USD savings' },
        ],
    },
    {
        label: 'Compliance & Risk',
        items: [
            { id: 'risk', title: 'Risk Assessment', subtitle: 'Per-asset risk, attack chains, P0–P3' },
            { id: 'compliance', title: 'Framework Scores', subtitle: 'Rollup against the major AI governance frameworks (EU AI Act, NIST AI RMF, ISO 42001)' },
            { id: 'framework_editor', title: 'Frameworks', subtitle: 'Browse, clone, edit, or build your own — Activation Wizard available inside' },
            // Activation Wizard is no longer a top-level nav item — it's a
            // one-time onboarding flow that lives as a CTA inside Frameworks.
            // The `framework_wiz` route below is still callable from in-product
            // links (e.g. Mode Dashboards empty-state, Frameworks page CTA).
            { id: 'evidence', title: 'Evidence Ledger', subtitle: 'Signed audit-grade evidence — controls, red-team runs, bias audits' },
            { id: 'policy_generator', title: 'AI Policy Generator', subtitle: 'FORESEE drafts your AI Acceptable Use Policy from the 10-dim profile + active frameworks. Sign to write a signed evidence row.' },
        ],
    },
    {
        label: 'Administration',
        items: [
            { id: 'extension', title: 'Endpoint Fleet', subtitle: 'Managed devices — browser plugin + endpoint scanner status, configuration, deployment artifacts' },
            { id: 'integrations', title: 'Integrations', subtitle: 'Enforcement & forwarders (Firewall / EDR / SIEM / M365 / GitHub / cloud AI) + AI Traffic Ingestion (SASE / DLP / vendor admin)' },
            { id: 'identity_link_reviews', title: 'Identity Link Reviews', subtitle: 'Triage cross-scope merges the auto-correlator refused (work ⇄ personal, etc.) — approve or reject' },
            { id: 'vendor_coverage', title: 'Vendor Enterprise Coverage', subtitle: 'Cross-reference your vendor seat list (Anthropic Enterprise today; OpenAI / Copilot / Vertex next) against NMT lane coverage — surfaces enrolled seats with no instrumentation + off-subscription users' },
            { id: 'agent_installation', title: 'Agent Installation', subtitle: 'NMT Claude Code / Cursor / Aider agent — install.sh, rotate, uninstall, verify per-machine — for any managed Mac/Linux endpoint' },
            { id: 'audit_log', title: 'Audit Log', subtitle: 'Immutable activity trail' },
        ],
    },
];

// Components mounted but hidden from primary nav. Accessible via deep
// links (`?go=auditor` etc.) for advanced/admin use. Promote back into
// NAV_GROUPS above if customers ask. Routes below still resolve them.
//
//   user_usage         → per-user view (overlaps Inventory + Event Stream)
//   raw_events         → debug event stream (use Policy Violations instead)
//   severity_tuning    → per-tenant niche setup (admin-only)
//   siem_forwarders    → folded under Integrations conceptually
//   auditor            → one-time issuance, lives inside Extension Admin flow
//
// SMB-focused trim (2026-06-29) — these used to be in NAV_GROUPS but
// they need data SMB tenants don't have. Routes still resolve so power
// users / Enterprise customers reach them via `?go=…`:
//   bias               → requires cohort uploads (no hosted models)
//   dsar               → requires consumer privacy intake
//   red_team           → requires hosted LLMs to attack
//   agent_traces       → populates only with Claude Code agent ingest
//   runtime_obs        → requires Gateway traffic; misleading empty until populated
//   idp_integrations   → no federation in scope for the SMB segment
//
// Drawer / sub-tab merged (in-portal access via the parent component's
// ⚙ / sub-tabs; sidebar slot removed, deep link still resolves):
//   cost_advisor_config → Cost Advisor → ⚙ Configure drawer
//   modes               → Policies → "Modes" sub-tab
//   dlp_ingestion       → Integrations → "AI Traffic Ingestion" sub-tab

const ALL_ITEMS = NAV_GROUPS.flatMap(g => g.items);

// Sidebar glyphs per nav id — rendered via <ModuleIcon> (inline SVG, no
// S3). The sidebar CSS forces `stroke: currentColor` so each icon adopts
// the nav item's text colour (idle / hover / active) automatically.
const NAV_ICONS = {
    home: 'dashboard',
    coverage: 'coverage',
    inventory: 'assets',
    signatures: 'fingerprint',
    policies: 'policy',
    group_enforcement: 'users',
    endpoint_enforcement: 'endpoint',
    llm_gateway: 'gateway',
    whitelist: 'allowlist',
    incidents: 'violation',
    budget: 'budget',
    cost_advisor: 'lightbulb',
    risk: 'risk',
    compliance: 'score',
    framework_editor: 'layers',
    evidence: 'ledger',
    policy_generator: 'wand',
    extension: 'fleet',
    integrations: 'integration',
    identity_link_reviews: 'link',
    vendor_coverage: 'building',
    agent_installation: 'install',
    audit_log: 'history',
};

// ── Wraps the lazy children so a slow import doesn't blank the right pane.
const LoadingPane = () => (
    <div className="aigp_empty">
        <div className="aigp_empty_title">Loading…</div>
    </div>
);

// ── Error boundary so one bad sub-view can't blank the whole portal.
class PaneErrorBoundary extends React.Component {
    state = { hasError: false, err: null };
    static getDerivedStateFromError(err) { return { hasError: true, err }; }
    componentDidCatch(err, info) { console.error('AIAdoptionPortal pane error:', err, info); }
    render() {
        if (this.state.hasError) {
            return (
                <div className="aigp_empty">
                    <div className="aigp_empty_title">This view hit an error</div>
                    <p className="aigp_empty_hint">{this.state.err?.message || 'Unknown error'}. Try another tab; this one will be fixed in the next release.</p>
                </div>
            );
        }
        return this.props.children;
    }
}

// ── Executive dashboard (the portal home).
// Pure light-theme. Just the four numbers an executive needs plus a clean
// "recent assets" table
// and a framework-readiness summary that links into the deeper views.
function ExecutiveDashboard({ onNavigate }) {
    const [data, setData] = useState(null);
    const [spenders, setSpenders] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recomputing, setRecomputing] = useState(false);
    const [recomputeMsg, setRecomputeMsg] = useState('');

    // The "AI adoption posture" framing leads with enablement, not
    // control — so we co-fetch the Cost Advisor's top-spenders surface
    // alongside the legacy posture dashboard. Spenders gives us the
    // numbers that matter to the CIO/CFO read: who's using AI, how much
    // volume, which models. The legacy dashboard provides the
    // discovery + risk + compliance backstop in the secondary band.
    //
    // Both calls swallow errors independently — a transient backend
    // hiccup on either endpoint must NOT crash the whole page render.
    // The dashboard degrades to "no data yet" if both fail; otherwise
    // we show whichever half came back.
    const fetchAll = () => Promise.all([
        ai_dashboard().then(r => unwrap(r) || null).catch(() => null),
        ai_load_cost_advisor_top_spenders({ days: 30, limit: 5 })
            .then(r => unwrap(r) || null)
            .catch(() => null),
    ]).then(([d, s]) => { setData(d); setSpenders(s); setLoading(false); })
        .catch(() => { setLoading(false); });

    useEffect(() => {
        let cancelled = false;
        fetchAll().then(() => { if (cancelled) return; });
        return () => { cancelled = true; };
    }, []);
    const fetchDashboard = fetchAll;

    // Manual recompute — audit-grade snapshot on demand. Event-driven hooks
    // (signals.py) keep aggregates fresh automatically; this is for the
    // moment an auditor watches you click a button and timestamps it.
    const runRecompute = async () => {
        setRecomputing(true);
        setRecomputeMsg('');
        try {
            const res = await ai_recompute();
            const result = unwrap(res);
            setRecomputeMsg(result?.message || 'Recompute complete.');
            await fetchDashboard();
        } catch (err) {
            setRecomputeMsg('Recompute failed. Check container logs.');
        }
        setRecomputing(false);
        setTimeout(() => setRecomputeMsg(''), 6000);
    };

    if (loading) return <LoadingPane />;
    if (!data) {
        return (
            <div className="aigp_empty">
                <div className="aigp_empty_title">No data yet</div>
                <p className="aigp_empty_hint">Run AI discovery to populate the portal. Start from Discovery → AI Inventory.</p>
            </div>
        );
    }

    const totalAssets = data.total_assets || 0;
    const totalEndpoints = data.total_endpoints || 0;
    const totalEndpointsRaw = data.total_endpoints_raw || totalEndpoints;
    const totalUsers = data.total_users || 0;
    const pending = data.shadow_ai || 0;
    const approvedTools = Math.max(0, totalAssets - pending);
    const sanctionedPct = totalAssets > 0 ? Math.round((approvedTools / totalAssets) * 100) : null;
    const violations = data.open_violations || 0;
    const eventsToday = data.events_today || 0;
    const highRisk = (data.risk_distribution?.high || 0) + (data.risk_distribution?.unacceptable || 0);
    const limitedRisk = data.risk_distribution?.limited || 0;
    const minimalRisk = data.risk_distribution?.minimal || 0;
    const recentAssets = (data.recent_assets || []).slice(0, 6);
    const frameworks = (data.framework_readiness || []).slice(0, 6);

    // Cost-advisor / volume signals — the headline numbers for the
    // adoption read. When the org is on subscription mode (flat fee),
    // spend is zero by design; surface call count + token volume instead.
    const spendersList = (spenders?.spenders || []);
    const totalCalls30d = spendersList.reduce((acc, s) => acc + (s.gateway_call_count || s.call_count || 0), 0);
    const totalTokens30d = spendersList.reduce((acc, s) => acc + (s.total_tokens || 0), 0);
    const totalExtEvents30d = spendersList.reduce((acc, s) => acc + (s.ext_event_count || 0), 0);
    const activeAIUsers = spendersList.filter(s => (s.gateway_call_count || 0) > 0 || (s.ext_event_count || 0) > 0).length;
    const billingMode = spenders?.billing_mode || 'pay_as_you_go';
    const isSubscription = billingMode === 'subscription';
    // Tool-tier coverage signal — distinct lanes captured this period.
    // Reads as "how much of the workforce's AI is governed" instead of
    // "what's broken".
    const sanctionedBlurb = sanctionedPct == null
        ? 'No tools discovered yet'
        : `${approvedTools} approved / ${totalAssets} discovered`;

    return (
        <div>
            {/* Recompute Now — admin safety net. Event-driven hooks keep
                aggregates fresh automatically; this is for the moment an
                auditor watches you click a button and write down a timestamp. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                {recomputeMsg && (
                    <span style={{ fontSize: 12, color: recomputeMsg.startsWith('Recompute failed') ? 'var(--color-danger)' : 'var(--color-success)' }}>
                        {recomputeMsg}
                    </span>
                )}
                <CommonButton text={recomputing ? 'Recomputing…' : '↻ Recompute Now'} onClick={runRecompute} disable={recomputing} />
            </div>

            {/* Headline strip — ADOPTION first. The four cards answer the
                CIO/CFO read: who's using AI, how much volume, are we
                governed, where's coverage. Risk + violations drop to the
                secondary band below — they still exist, they just stop
                being the page lede so the room reads enablement before
                control. Reflects the rev-11 management briefing positioning. */}
            <div className="aigp_kpi_strip">
                {[
                    {
                        icon: 'users',
                        label: 'Active AI users',
                        value: activeAIUsers || totalUsers,
                        trend: 'Last 30 days · gateway + extension + agent',
                        go: 'cost_advisor',
                    },
                    {
                        icon: 'gateway',
                        label: 'AI calls (30d)',
                        value: totalCalls30d > 0 ? totalCalls30d.toLocaleString() : (eventsToday || '—'),
                        trend: totalTokens30d > 0
                            ? `${(totalTokens30d / 1e9).toFixed(1)}B tokens routed`
                            : totalExtEvents30d > 0
                                ? `${totalExtEvents30d.toLocaleString()} browser-extension events`
                                : 'No metered activity yet',
                        go: 'cost_advisor',
                    },
                    {
                        icon: 'allowlist',
                        label: 'Sanctioned tool coverage',
                        value: sanctionedPct == null ? '—' : `${sanctionedPct}%`,
                        trend: sanctionedBlurb,
                        go: 'inventory',
                    },
                    {
                        icon: 'endpoint',
                        label: 'Endpoint coverage',
                        value: totalEndpoints,
                        trend: totalEndpoints === 0
                            ? 'No endpoints reporting'
                            : totalEndpointsRaw > totalEndpoints
                                ? `${totalEndpoints} unique · ${totalEndpointsRaw} install records`
                                : 'Plugin + Scanner + Agent across lanes',
                        go: 'extension',
                    },
                ].map(k => (
                    <AddOnCard
                        key={k.label}
                        icon={k.icon}
                        title={k.label}
                        onClick={() => onNavigate(k.go)}
                        label={(
                            <>
                                <div style={{ fontSize: fontSize.xs, fontWeight: fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.textMuted }}>
                                    {k.label}
                                </div>
                                <div style={{ fontSize: fontSize.xs, fontWeight: fontWeight.regular, color: T.textMuted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {k.trend}
                                </div>
                            </>
                        )}
                        action={(
                            <span style={{ fontFamily, fontSize: fontSize['4xl'], fontWeight: fontWeight.semibold, color: T.textPrimary, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
                                {k.value}
                            </span>
                        )}
                    />
                ))}
            </div>

            {/* Secondary band — risk + violations + 24h pulse. Demoted
                from headline-tier per the adoption-first positioning, but
                kept one click from page-load for the security viewer. */}
            <div style={{
                display: 'flex', gap: 18, marginTop: 12, padding: '10px 14px',
                background: 'var(--color-surface-muted)',
                border: '1px solid var(--color-border)',
                borderRadius: 8, fontSize: 12, color: 'var(--color-text-secondary)',
                flexWrap: 'wrap',
            }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', alignSelf: 'center' }}>
                    Risk + posture
                </span>
                <a onClick={() => onNavigate('inventory')} style={{ cursor: 'pointer' }}>
                    <strong style={{ color: highRisk > 0 ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>{highRisk}</strong>{' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>high-risk tools</span>
                </a>
                <a onClick={() => onNavigate('incidents')} style={{ cursor: 'pointer' }}>
                    <strong style={{ color: violations > 0 ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>{violations}</strong>{' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>open violations</span>
                </a>
                <a onClick={() => onNavigate('inventory')} style={{ cursor: 'pointer' }}>
                    <strong style={{ color: pending > 0 ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>{pending}</strong>{' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>tools pending review</span>
                </a>
                <a onClick={() => onNavigate('raw_events')} style={{ cursor: 'pointer' }}>
                    <strong style={{ color: 'var(--color-text-secondary)' }}>{eventsToday || 0}</strong>{' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>events in last 24h</span>
                </a>
                {isSubscription && (
                    <span style={{ marginLeft: 'auto', color: 'var(--color-primary)', fontWeight: 500 }}>
                        Subscription mode — $ math suppressed, usage tracked
                    </span>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                {/* WHERE ADOPTION IS HAPPENING — replaces the legacy
                    "AI Inventory health" panel as the LEFT-column hero.
                    Shows the top Persons + their model mix so the CIO
                    reads adoption depth, not control debt. Clicking a row
                    drills into the per-Person Cost Advisor. */}
                <div className="aigp_card" style={{ padding: 0 }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Where adoption is happening</h4>
                        <CommonButton text="Open Cost Advisor →" onClick={() => onNavigate('cost_advisor')} />
                    </div>
                    {spendersList.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
                            No metered AI activity yet. Issue gateway keys (LLM Gateway →
                            Bulk-issue from IdP) or deploy the browser extension /
                            Claude Code agent to start tracking usage.
                        </div>
                    ) : (
                        <div style={{ padding: '8px 16px 14px' }}>
                            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                                Top users · last 30 days
                            </div>
                            {spendersList.slice(0, 5).map((s, i) => {
                                const calls = (s.gateway_call_count || s.call_count || 0);
                                const ext = (s.ext_event_count || 0);
                                const total = calls + ext;
                                const totalAll = spendersList.reduce((a, x) => a + (x.gateway_call_count || x.call_count || 0) + (x.ext_event_count || 0), 0) || 1;
                                const pct = Math.round((total / totalAll) * 100);
                                return (
                                    <div key={s.person_id} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 80px 60px',
                                        gap: 10, alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: i < Math.min(4, spendersList.length - 1) ? '1px solid var(--color-border)' : 'none',
                                    }}>
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                                {s.email || s.name || `person:${s.person_id}`}
                                                <span style={{
                                                    marginLeft: 6, fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
                                                    padding: '1px 6px', borderRadius: 4,
                                                    background: s.identity_type === 'work' ? 'rgba(91,141,253,0.15)' : 'rgba(245,158,11,0.15)',
                                                    color: s.identity_type === 'work' ? '#7aa7ff' : '#f59e0b'
                                                }}>
                                                    {s.identity_type || 'user'}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
                                                {s.top_vendor || '—'} {s.top_model ? `· ${s.top_model}` : ''}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--color-text-secondary)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                                            {calls > 0 ? `${calls.toLocaleString()} calls` : `${ext.toLocaleString()} events`}
                                        </div>
                                        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--color-text-muted)' }}>
                                            {pct}%
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Compliance posture — renamed from "Framework readiness".
                    Same horizontal-bar viz; the heading now reads as a
                    compliance-enablement signal rather than a gating one. */}
                <div className="aigp_card" style={{ padding: 0 }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Compliance posture</h4>
                        <button
                            onClick={() => onNavigate('compliance')}
                            style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                            See all →
                        </button>
                    </div>
                    {frameworks.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
                            No frameworks configured.
                        </div>
                    ) : (
                        <div style={{ padding: '8px 16px 12px' }}>
                            {frameworks.map(fw => {
                                const pct = fw.evaluated ? Math.round(fw.readiness_pct || 0) : null;
                                const barColor =
                                    pct == null ? 'var(--color-surface-hover)' :
                                        pct >= 80 ? 'var(--color-success)' :
                                            pct >= 50 ? 'var(--color-warning)' :
                                                'var(--color-danger)';
                                return (
                                    <div key={fw.code} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '120px 1fr 70px',
                                        gap: 12, alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: '1px solid var(--color-border)',
                                    }}>
                                        <div style={{ fontSize: 12, color: 'var(--color-text-primary)', fontWeight: 500 }}>{fw.label}</div>
                                        <div style={{ height: 6, background: 'var(--color-surface-hover)', borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{
                                                width: pct == null ? '0%' : `${pct}%`,
                                                height: '100%',
                                                background: barColor,
                                                borderRadius: 3,
                                                transition: 'width 400ms ease',
                                            }} />
                                        </div>
                                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', textAlign: 'right' }}>
                                            {pct == null ? <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Not run</span> : `${pct}%`}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Discovery + onboarding funnel — adoption-flavoured rephrase
                of the legacy "Inventory health" panel. Shadow → approved
                is the storyline of how the org is bringing AI into the
                sanctioned envelope. */}
            {totalAssets > 0 && (
                <div className="aigp_card" style={{ marginTop: 16, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Discovery + onboarding funnel</h4>
                        <button onClick={() => onNavigate('inventory')}
                            style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                            Open Inventory →
                        </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        <strong style={{ color: 'var(--color-text-primary)' }}>{totalAssets}</strong> AI tools discovered
                        <span style={{ color: 'var(--color-text-muted)' }}>→</span>
                        <strong style={{ color: pending > 0 ? 'var(--color-warning)' : 'var(--color-text-primary)' }}>{pending}</strong> pending review
                        <span style={{ color: 'var(--color-text-muted)' }}>→</span>
                        <strong style={{ color: approvedTools > 0 ? 'var(--color-success)' : 'var(--color-text-primary)' }}>{approvedTools}</strong> approved
                        {recentAssets[0]?.name && (
                            <span style={{ marginLeft: 'auto', color: 'var(--color-text-muted)', fontSize: 11 }}>
                                Last discovered: <strong style={{ color: 'var(--color-text-secondary)' }}>{recentAssets[0].name}</strong>
                                {(recentAssets[0]?.last_detected_at || recentAssets[0]?.created_at)
                                    ? <span> · {new Date(recentAssets[0]?.last_detected_at || recentAssets[0]?.created_at).toLocaleDateString()}</span>
                                    : null}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* EU AI Act risk distribution — single clean stacked bar.
                Reads at a glance; demoted from the headline tier. */}
            {(highRisk + limitedRisk + minimalRisk) > 0 && (
                <div className="aigp_card" style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>EU AI Act risk distribution</h4>
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{highRisk + limitedRisk + minimalRisk} classified</span>
                    </div>
                    {(() => {
                        const total = highRisk + limitedRisk + minimalRisk;
                        const seg = (count, color, label) => count > 0 ? (
                            <div title={`${label}: ${count}`} style={{ width: `${(count / total) * 100}%`, background: color }} />
                        ) : null;
                        return (
                            <>
                                <div style={{ display: 'flex', height: 10, background: 'var(--color-surface-hover)', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
                                    {seg(highRisk, 'var(--color-danger)', 'High + unacceptable')}
                                    {seg(limitedRisk, 'var(--color-warning)', 'Limited')}
                                    {seg(minimalRisk, 'var(--color-success)', 'Minimal')}
                                </div>
                                <div style={{ display: 'flex', gap: 18, fontSize: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 10, height: 10, background: 'var(--color-danger)', borderRadius: 2 }} />
                                        <span style={{ color: 'var(--color-text-secondary)' }}>High risk · <strong style={{ color: 'var(--color-text-primary)' }}>{highRisk}</strong></span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 10, height: 10, background: 'var(--color-warning)', borderRadius: 2 }} />
                                        <span style={{ color: 'var(--color-text-secondary)' }}>Limited · <strong style={{ color: 'var(--color-text-primary)' }}>{limitedRisk}</strong></span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 10, height: 10, background: 'var(--color-success)', borderRadius: 2 }} />
                                        <span style={{ color: 'var(--color-text-secondary)' }}>Minimal · <strong style={{ color: 'var(--color-text-primary)' }}>{minimalRisk}</strong></span>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}

// ── Main portal component
export default function AIAdoptionPortal() {
    const navigate = useNavigate();

    // Initial active id is resolved at mount from (in priority order):
    //   1. `?go=<id>` query param  — deep-link CTA from elsewhere
    //   2. `aigp:active`           — localStorage, persisted across refreshes
    //   3. 'home'                  — first-ever visit
    // Computed inside useState so we don't render the wrong pane on tick 0
    // and then flicker to the right one on tick 1.
    const [active, setActive] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const fromQuery = params.get('go');
            if (fromQuery && ALL_ITEMS.some(i => i.id === fromQuery)) return fromQuery;
            const fromStorage = window.localStorage.getItem('aigp:active');
            if (fromStorage && ALL_ITEMS.some(i => i.id === fromStorage)) return fromStorage;
        } catch { /* SSR / sandboxed env */ }
        return 'home';
    });
    const activeItem = useMemo(() => ALL_ITEMS.find(i => i.id === active) || ALL_ITEMS[0], [active]);

    // ── Pending identity-link reviews — hide-when-zero + badge pattern.
    // Auto-correlator dumps work⇄personal-email merge candidates here.
    // Tab only earns its sidebar slot when there's something to act on;
    // when count > 0 it renders with a red badge to draw the eye. We
    // refetch when the user navigates to/from the tab so completing a
    // review hides the tab the moment the queue is drained.
    // Plan info for the header badge — same source the dashboard uses.
    const [planInfo, setPlanInfo] = useState(null);
    useEffect(() => {
        let cancelled = false;
        getUserPlanInfoService()
            .then(response => {
                if (cancelled) return;
                const info = response?.statusCode?.response_data?.data?.plan_info;
                if (info) setPlanInfo(info);
            })
            .catch(() => { /* header just omits the badge on failure */ });
        return () => { cancelled = true; };
    }, []);

    const [linkReviewCount, setLinkReviewCount] = useState(0);
    useEffect(() => {
        let cancelled = false;
        ai_load_link_reviews({ status: 'pending' })
            .then(r => {
                if (cancelled) return;
                const data = unwrap(r);
                const rows = (data && (data.results || data.rows || data)) || [];
                setLinkReviewCount(Array.isArray(rows) ? rows.length : 0);
            })
            .catch(() => { /* keep 0 — don't blank the page on a transient failure */ });
        return () => { cancelled = true; };
    }, [active]);  // re-fetch whenever the operator switches tabs

    // Filter sidebar: identity_link_reviews only shows when there's
    // something to act on. The route stays mounted so deep links keep
    // working (?go=identity_link_reviews) when an operator wants to
    // browse historical reviews.
    const visibleNavGroups = useMemo(() =>
        NAV_GROUPS.map(g => ({
            ...g,
            items: g.items.filter(i =>
                i.id !== 'identity_link_reviews' || linkReviewCount > 0,
            ),
        })).filter(g => g.items.length > 0),
        [linkReviewCount],
    );

    // Attach dynamic badges (identity-link review queue count) so the
    // generic CommonSidebar stays portal-agnostic — it just renders
    // whatever `badge` each item carries.
    const sidebarGroups = useMemo(() =>
        visibleNavGroups.map(g => ({
            ...g,
            items: g.items.map(i => {
                const svgIcon = NAV_ICONS[i.id]
                    ? <ModuleIcon name={NAV_ICONS[i.id]} size={18} />
                    : undefined;
                return i.id === 'identity_link_reviews'
                    ? { ...i, svgIcon, badge: linkReviewCount }
                    : { ...i, svgIcon };
            }),
        })),
        [visibleNavGroups, linkReviewCount],
    );

    // Persist the active id every time it changes so a refresh comes back
    // to the same pane. Also keeps the URL bar tidy — strips the one-shot
    // `?go=...` after it has been consumed.
    useEffect(() => {
        try {
            window.localStorage.setItem('aigp:active', active);
            const params = new URLSearchParams(window.location.search);
            if (params.has('go')) {
                params.delete('go');
                const qs = params.toString();
                window.history.replaceState(
                    window.history.state, '',
                    window.location.pathname + (qs ? `?${qs}` : ''),
                );
            }
        } catch { /* SSR / sandboxed env */ }
    }, [active]);

    const renderPane = () => {
        switch (active) {
            case 'home': return <ExecutiveDashboard onNavigate={setActive} />;
            case 'inventory': return <div className="aigp_legacy_wrap"><AIInventory onNavigate={setActive} /></div>;
            case 'user_usage': return <div className="aigp_legacy_wrap"><AIUserUsage /></div>;
            case 'extension': return <div className="aigp_legacy_wrap"><AIExtensionAdmin /></div>;
            case 'severity_tuning': return <SeverityTuningView />;
            case 'siem_forwarders': return <SIEMForwardersView />;
            case 'raw_events': return <RawEventsView />;
            case 'incidents': return <div className="aigp_legacy_wrap"><AIIncidents /></div>;
            case 'whitelist': return <div className="aigp_legacy_wrap"><AIWhitelist /></div>;
            case 'policies': return <div className="aigp_legacy_wrap"><AIPolicies /></div>;
            case 'modes': return <div className="aigp_legacy_wrap"><ModeDashboards /></div>;
            case 'group_enforcement': return <div className="aigp_legacy_wrap"><AIGroupEnforcement /></div>;
            case 'endpoint_enforcement': return <div className="aigp_legacy_wrap"><AIEndpointEnforcement /></div>;
            case 'idp_integrations': return <div className="aigp_legacy_wrap"><AIIdPIntegrations /></div>;
            case 'llm_gateway': return <div className="aigp_legacy_wrap"><AILLMGateway /></div>;
            case 'agent_traces': return <div className="aigp_legacy_wrap"><AIAgentTrace /></div>;
            case 'budget': return <div className="aigp_legacy_wrap"><AIBudget /></div>;
            case 'cost_advisor': return <div className="aigp_legacy_wrap"><AICostAdvisor /></div>;
            case 'coverage': return <div className="aigp_legacy_wrap"><AICoveragePosture /></div>;
            case 'risk': return <div className="aigp_legacy_wrap"><AIRiskAssessment /></div>;
            case 'compliance': return <div className="aigp_legacy_wrap"><AIRiskCompliance /></div>;
            case 'policy_generator': return <div className="aigp_legacy_wrap"><AIPolicyGenerator /></div>;
            case 'framework_editor': return <FrameworkEditor onNavigate={setActive} />;
            case 'framework_wiz': return <div className="aigp_legacy_wrap"><FrameworkWizard /></div>;
            // Runtime observability consolidates Response Eval / Sentiment /
            // Drift behind one nav slot. Legacy sub-tab ids still resolve
            // by passing them as `initialTab` so existing in-product links
            // (e.g. from Event Stream → "see related eval") keep working.
            case 'runtime_obs':
            case 'response_eval':
            case 'sentiment':
            case 'drift':
                return <RuntimeObservability initialTab={active === 'runtime_obs' ? 'response_eval' : active} />;
            case 'evidence': return <div className="aigp_legacy_wrap"><EvidenceLedger /></div>;
            case 'integrations': return <div className="aigp_legacy_wrap"><AIIntegrations /></div>;
            case 'dlp_ingestion': return <div className="aigp_legacy_wrap"><AIDlpIntegrations /></div>;
            case 'identity_link_reviews': return <div className="aigp_legacy_wrap"><AIIdentityLinkReviews /></div>;
            case 'cost_advisor_config': return <div className="aigp_legacy_wrap"><AICostAdvisorConfig /></div>;
            case 'agent_installation': return <div className="aigp_legacy_wrap"><AIAgentInstallation /></div>;
            case 'vendor_coverage': return <div className="aigp_legacy_wrap"><AIVendorCoverage /></div>;
            case 'auditor': return <div className="aigp_legacy_wrap"><AuditorTokens /></div>;
            case 'audit_log': return <div className="aigp_legacy_wrap"><AIAuditLog /></div>;
            case 'signatures': return <div className="aigp_legacy_wrap"><AISignatures /></div>;
            case 'red_team': return <div className="aigp_legacy_wrap"><AIRedTeam /></div>;
            case 'dsar': return <div className="aigp_legacy_wrap"><AIDsar /></div>;
            case 'bias': return <div className="aigp_legacy_wrap"><AIBias /></div>;
            default: return <ExecutiveDashboard onNavigate={setActive} />;
        }
    };

    return (
        <div className="aigp_root">
            {/* Left rail — shared navy sidebar, spans the full height (sidebar
                grid area). Scrolls internally; overrides the base .sidebar
                100vh height / grid-area to sit in the grid column. */}
            <CommonSidebar
                groups={sidebarGroups}
                activeId={active}
                onSelect={setActive}
                ariaLabel="AI Adoption navigation"
                subtitle="AI Adoption"
                style={{
                    gridArea: 'sidebar',
                    height: '100%',
                    minHeight: 0,
                    overflowY: 'auto',
                }}
            />

            {/* Top bar — shared CommonHeader over the content column only.
                currentScreen shows the active section; org chip + Back-to-
                Dashboard passed in; profile (avatar) renders by default. */}
            <CommonHeader
                className="aigp_header"
                planInfo={planInfo}
                currentScreen={<span className="aigp_header_title">{activeItem.title}</span>}
                org={<span className="aigp_tenant_chip">Org: <strong>Foresee Security</strong></span>}
                backToDashboard={<CommonButton text="← Back to Dashboard" onClick={() => navigate('/product-dashboard')} />}
            />

            {/* Content pane */}
            <main className="aigp_pane">
                {/* <div className="aigp_pane_header">
                    <div>
                        <h2 className="aigp_pane_title">{activeItem.title}</h2>
                        <p className="aigp_pane_subtitle">{activeItem.subtitle}</p>
                    </div>
                </div> */}
                <PaneErrorBoundary key={active}>
                    <Suspense fallback={<LoadingPane />}>
                        {renderPane()}
                    </Suspense>
                </PaneErrorBoundary>
            </main>
        </div>
    );
}
